import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import { getApiErrorData } from '../../services/apiError';
import type { WxbDataTableProps } from '../wxb-ui';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbIcon,
  WxbKpiCard,
  WxbModal,
  WxbSpinner,
  WxbTag,
  WxbUpload,
  wxbToast,
} from '../wxb-ui';
import type { Employee, OrganizationUnitNode } from '../../types/organizationWorkbench';
import { fetchEmployeeRoleOptions, type EmployeeRoleOption } from './employeeRoleHelpers';
import {
  downloadEmployeeTemplate,
  MAX_EMPLOYEE_IMPORT_FILE_BYTES,
  MAX_EMPLOYEE_IMPORT_ROWS,
  readEmployeeImportWorkbook,
  validateEmployeeImportRows,
  type EmployeeImportPreviewRow,
} from './employeeWorkbook';
import './EmployeeImportModal.css';

interface EmployeeImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => Promise<void> | void;
  employees: Employee[];
  units: OrganizationUnitNode[];
  defaultUnitId: number | null;
}

type ImportState = 'ready' | 'error' | 'created' | 'failed';

interface ImportRow extends EmployeeImportPreviewRow {
  importState: ImportState;
  resultMessage: string | null;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const data = getApiErrorData<{ error?: unknown; message?: unknown }>(error);
  const message = data?.error ?? data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const EmployeeImportModal: React.FC<EmployeeImportModalProps> = ({
  open,
  onClose,
  onImported,
  employees,
  units,
  defaultUnitId,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [roles, setRoles] = useState<EmployeeRoleOption[]>([]);
  const [preview, setPreview] = useState<ImportRow[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview([]);
    setErrorMessage(null);
  };

  const close = () => {
    if (previewing || importing) return;
    reset();
    onClose();
  };

  const loadRoles = useCallback(async (): Promise<EmployeeRoleOption[]> => {
    setLoadingRoles(true);
    try {
      const nextRoles = await fetchEmployeeRoleOptions();
      setRoles(nextRoles);
      return nextRoles;
    } catch (error) {
      wxbToast.error(getErrorMessage(error, '岗位列表加载失败'));
      return [];
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRoles();
  }, [loadRoles, open]);

  const runPreview = async (selected: File) => {
    setPreviewing(true);
    setErrorMessage(null);
    try {
      const sourceRows = await readEmployeeImportWorkbook(selected);
      const availableRoles = roles.length > 0 ? roles : await loadRoles();
      if (availableRoles.length === 0) {
        setPreview([]);
        setErrorMessage('未能读取岗位配置，暂时无法校验导入文件');
        return;
      }

      const rows = validateEmployeeImportRows(sourceRows, {
        existingEmployeeCodes: employees.map((employee) => employee.employee_code),
        roles: availableRoles.map((role) => ({ roleCode: role.role_code })),
        units,
        defaultUnitId,
      }).map((row) => ({
        ...row,
        importState: row.issues.length > 0 ? 'error' as const : 'ready' as const,
        resultMessage: null,
      }));
      setPreview(rows);
    } catch (error) {
      setPreview([]);
      setErrorMessage(error instanceof Error ? error.message : '文件预检失败，请检查文件后重试');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    const selected = files[0];
    if (!selected || previewing || importing) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      reset();
      setErrorMessage('只接受 .xlsx 文件，请使用系统下载的模板。');
      return;
    }
    if (selected.size > MAX_EMPLOYEE_IMPORT_FILE_BYTES) {
      reset();
      setErrorMessage('文件不能超过 5 MB。');
      return;
    }
    setFile(selected);
    await runPreview(selected);
  };

  const handleImport = async () => {
    const rowsToCreate = preview.filter((row) => row.importState === 'ready');
    if (rowsToCreate.length === 0 || importing) return;

    setImporting(true);
    let created = 0;
    let failed = 0;
    for (const row of rowsToCreate) {
      try {
        await api.post('/employees', {
          employeeCode: row.employeeCode,
          employeeName: row.employeeName,
          unitId: row.unitId,
          orgRole: row.roleCode,
          employmentStatus: row.employmentStatus,
          hireDate: row.hireDate,
          shopfloorBaselinePct: row.shopfloorBaselinePct,
          shopfloorUpperPct: row.shopfloorUpperPct,
        });
        created += 1;
        setPreview((current) => current.map((item) => item.row === row.row
          ? { ...item, importState: 'created', resultMessage: '已新增' }
          : item));
      } catch (error) {
        failed += 1;
        setPreview((current) => current.map((item) => item.row === row.row
          ? { ...item, importState: 'failed', resultMessage: getErrorMessage(error, '新增失败') }
          : item));
      }
    }

    try {
      if (created > 0) await onImported();
      wxbToast[failed > 0 ? 'warning' : 'success'](
        failed > 0 ? `导入完成：新增 ${created} 行，失败 ${failed} 行` : `导入完成：新增 ${created} 名员工`,
      );
    } finally {
      setImporting(false);
    }
  };

  const columns = useMemo<WxbDataTableProps<ImportRow>['columns']>(() => [
    { title: 'Excel 行', dataIndex: 'row', width: 88 },
    {
      title: '员工',
      key: 'employee',
      width: 220,
      render: (_: unknown, row: ImportRow) => (
        <div className="employee-import-modal__employee-cell">
          <strong>{row.employeeName || '未填写姓名'}</strong>
          <span>{row.employeeCode || '未填写编号'}</span>
        </div>
      ),
    },
    { title: '岗位', dataIndex: 'roleCode', width: 160 },
    { title: '组织单元 ID', dataIndex: 'unitId', width: 140, render: (unitId: number | null) => unitId ?? '—' },
    {
      title: '结果',
      dataIndex: 'importState',
      width: 120,
      render: (state: ImportState) => {
        const meta = {
          ready: { color: 'blue' as const, label: '待导入' },
          error: { color: 'red' as const, label: '需修正' },
          created: { color: 'green' as const, label: '已新增' },
          failed: { color: 'red' as const, label: '导入失败' },
        }[state];
        return <WxbTag color={meta.color}>{meta.label}</WxbTag>;
      },
    },
    {
      title: '处理说明',
      key: 'issues',
      render: (_: unknown, row: ImportRow) => (
        <span className={row.issues.length > 0 || row.importState === 'failed'
          ? 'employee-import-modal__error-text'
          : 'employee-import-modal__muted-text'}>
          {row.issues.length > 0 ? row.issues.join('；') : row.resultMessage || '校验通过'}
        </span>
      ),
    },
  ], []);

  const readyCount = preview.filter((row) => row.importState === 'ready').length;
  const errorCount = preview.filter((row) => row.importState === 'error' || row.importState === 'failed').length;
  const createdCount = preview.filter((row) => row.importState === 'created').length;
  const canImport = readyCount > 0 && errorCount === 0;

  return (
    <WxbModal
      open={open}
      title="批量导入员工"
      width={980}
      onCancel={close}
      maskClosable={!previewing && !importing}
      closable={!previewing && !importing}
      footer={(
        <div className="employee-import-modal__footer">
          <WxbButton type="button" variant="ghost" onClick={close} disabled={previewing || importing}>关闭</WxbButton>
          <WxbButton type="button" variant="primary" onClick={handleImport} disabled={!canImport || previewing || importing || loadingRoles}>
            {importing ? '正在导入…' : `确认导入${readyCount > 0 ? `（${readyCount}）` : ''}`}
          </WxbButton>
        </div>
      )}
    >
      <div className="employee-import-modal__body">
        <WxbAlert title="先预检，再写入">
          系统会校验员工编号、岗位编码、组织单元和格式。若有错误行，请修正文件后重新预检；校验通过后才可导入。
        </WxbAlert>

        <section className="employee-import-modal__step">
          <div className="employee-import-modal__step-heading">
            <div>
              <h3>1. 下载并填写模板</h3>
              <p>组织单元 ID 可留空，留空时员工将归属到当前选中的组织单元。</p>
            </div>
            <WxbButton type="button" variant="secondary" onClick={downloadEmployeeTemplate}>下载 XLSX 模板</WxbButton>
          </div>
        </section>

        <section className="employee-import-modal__step">
          <div className="employee-import-modal__step-heading">
            <div>
              <h3>2. 上传并自动预检</h3>
              <p>仅接受不超过 5 MB 的 .xlsx 文件，最多 {MAX_EMPLOYEE_IMPORT_ROWS} 行。</p>
            </div>
            {file && <WxbButton type="button" variant="ghost" size="sm" onClick={reset} disabled={previewing || importing}>清除文件</WxbButton>}
          </div>
          <WxbUpload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple={false}
            onFilesSelected={handleFilesSelected}
            fileList={file ? [{
              uid: `${file.name}-${file.lastModified}`,
              name: file.name,
              size: file.size,
              status: previewing ? 'uploading' : errorMessage || errorCount > 0 ? 'error' : 'done',
            }] : []}
            className="employee-import-modal__upload"
          >
            <div className="employee-import-modal__upload-content">
              <WxbIcon name="batch-record" size={34} aria-hidden="true" />
              <strong>{file ? '选择其他 XLSX 文件' : '选择或拖入 XLSX 文件'}</strong>
              <span>上传后自动校验岗位、组织单元、员工编号和字段格式</span>
            </div>
          </WxbUpload>
        </section>

        {previewing && <WxbSpinner tip="正在预检工作簿…" />}
        {errorMessage && <WxbAlert variant="error" title="文件未通过检查">{errorMessage}</WxbAlert>}

        {preview.length > 0 && !previewing && (
          <section className="employee-import-modal__step" aria-labelledby="employee-import-preview-title">
            <div className="employee-import-modal__step-heading">
              <div>
                <h3 id="employee-import-preview-title">3. 确认预检结果</h3>
                <p>Excel 行号可直接用于定位并修正原文件。</p>
              </div>
              <WxbButton type="button" variant="secondary" size="sm" onClick={() => file && runPreview(file)} disabled={previewing || importing}>重新预检</WxbButton>
            </div>

            <div className="employee-import-modal__summary">
              <WxbKpiCard title="数据行" value={preview.length} />
              <WxbKpiCard title="待导入" value={readyCount} />
              <WxbKpiCard title="错误行" value={errorCount} />
              <WxbKpiCard title="已新增" value={createdCount} />
            </div>

            <WxbAlert variant={canImport ? 'warning' : 'error'} title={canImport ? '预检通过，可以确认导入' : '预检未通过，请先修正错误行'}>
              {canImport ? `将新增 ${readyCount} 名员工。` : '存在阻断错误时，系统不会开始导入。'}
            </WxbAlert>

            <WxbDataTable<ImportRow>
              rowKey={(row) => `${row.row}-${row.employeeCode}`}
              density="compact"
              columns={columns}
              dataSource={preview}
              pagination={preview.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
              scroll={{ x: 960, y: 300 }}
            />
          </section>
        )}
      </div>
    </WxbModal>
  );
};

export default EmployeeImportModal;
